import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Link,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { orderBy } from "lodash";

import store from "../../../store";
import { getCustomerInfo, updateCustomerInfo } from "../../../server/axios";
import { Header, MainColorButton, Loading } from "../../../components";
import { bottomButton } from "../../../common/styles";
import BaseInfoArea from "../baseInfoArea";
import { CUSTOMER_RESULT, ERROR, FAMILY_ADDER } from "../../../routes/path";
import { CUSTOMER_INFO_INDEX_AND_NAME } from "../../../common/constants";

/**
 * 利用者情報登録画面
 * @returns
 */
const CustomerUpdater = () => {
  const navigate = useNavigate();
  const location = useLocation();

  //初期データ
  const [data, setData] = useState({
    //姓
    lastName: location.state?.lastName || "",
    //名
    firstName: location.state?.firstName || "",
    //姓かな
    lastNameKana: location.state?.lastNameKana || "",
    //名かな
    firstNameKana: location.state?.firstNameKana || "",
    //性別
    gender: location.state?.gender || "",
    //生年月日
    birthday: location.state?.birthday || null,
    //減免フラグ
    exemptionFlg: location.state?.exemptionFlg,
    //郵便番号
    postalCode: location.state?.postalCode || "",
    //都道府県
    prefecture: location.state?.prefecture || "",
    //市区町村
    municipalities: location.state?.municipalities || "",
    //町名番地
    townNameAddress: location.state?.townNameAddress || "",
    //建物名等
    buildingNameEtc: location.state?.buildingNameEtc || "",
    //電話番号
    phoneNumber: location.state?.phoneNumber || "",
    //家族情報
    families: location.state?.families || [],
  });

  const getData = () => {
    setLoadingOpen(true);
    const selectLineInfo = (state) => state.prescription.lineInfo;
    const lineInfo = selectLineInfo(store.getState());
    getCustomerInfo({ lineId: lineInfo.lineId }).then((res) => {
      setData({
        siteId: res.data.siteId,
        customerId: res.data.customerId,
        updateDatetime: res.data.updateDatetime,
        lastName: res.data.lastName || "",
        firstName: res.data.firstName || "",
        lastNameKana: res.data.lastNameKana || "",
        firstNameKana: res.data.firstNameKana || "",
        gender: res.data.gender || "",
        birthday: res.data.birthday || null,
        exemptionFlg: res.data.exemptionFlg,
        postalCode: res.data.postalCode || "",
        prefecture: res.data.prefecture || "",
        municipalities: res.data.municipalities || "",
        townNameAddress: res.data.townNameAddress || "",
        buildingNameEtc: res.data.buildingNameEtc || "",
        phoneNumber: res.data.phoneNumber || "",
        families: res.data.families || [],
      });
      setLoadingOpen(false);
    });
  };

  const setExclusionErrorMessage = () => {
    setOpenSnackbar({
      status: true,
      message: (
        <>
          データが変わりました。
          <Link
            onTouchEnd={() => {
              getData();
              handleSnackbarClose();
            }}
          >
            リロード
          </Link>
          して再度登録してください。
        </>
      ),
    });
  };

  useEffect(() => {
    if (
      (!location.state?.siteId && !data.siteId) ||
      (!location.state?.customerId && !data.customerId) ||
      (!location.state?.updateDatetime && !data.updateDatetime)
    ) {
      navigate(ERROR);
    }
    const selectExclusionError = (state) =>
      state.prescription.exclusionErrorFlag;
    const exclusionErrorFlag = selectExclusionError(store.getState());
    //家族情報変更排他エラー
    if (exclusionErrorFlag) {
      setExclusionErrorMessage();
    }
    //家族情報更新
    const selectFamiliesData = (state) => state.prescription.familiesData;
    const familiesData = selectFamiliesData(store.getState());
    setData({ ...data, families: familiesData });
  }, []);

  const { siteId, customerId, updateDatetime } = location.state;
  const [endY, setEndY] = useState(0);
  //Loadingコンポネント表示非表示state
  const [loadingOpen, setLoadingOpen] = useState(false);
  //エラーメッセージ表示
  const [openSnackbar, setOpenSnackbar] = useState({
    status: false,
    message: location.state?.siteId || "",
  });
  const handleSnackbarClose = () => {
    setOpenSnackbar({
      status: false,
      message: location.state?.siteId || "",
    });
  };

  const updateInfo = () => {
    if (data.families && data.families.length > 0) {
      // 家族の中に、同姓同名と同じ生年月日の状況をチェック
      if (
        data.families.find(
          (s) =>
            s.lastName === data.lastName &&
            s.firstName === data.firstName &&
            s.birthday === data.birthday
        )
      ) {
        setOpenSnackbar({
          status: true,
          message: "名前と生年月日がご登録済の家族の情報と重複しています。",
        });
        return;
      }
    }

    // 必須入力チェック
    const emptyCustomerInfo = [
      data.lastName,
      data.firstName,
      data.lastNameKana,
      data.firstNameKana,
      data.gender,
      data.birthday,
      data.postalCode,
      data.prefecture,
      data.municipalities,
      data.townNameAddress,
      data.phoneNumber,
      data.exemptionFlg,
    ]
      .map((info, index) => {
        if (typeof info === "boolean") {
          return info === null
            ? CUSTOMER_INFO_INDEX_AND_NAME[index]
            : undefined;
        } else {
          return !info ? CUSTOMER_INFO_INDEX_AND_NAME[index] : undefined;
        }
      })
      .filter((index) => index !== undefined);
    if (emptyCustomerInfo.length > 0) {
      setOpenSnackbar({
        status: true,
        message: (
          <>
            以下の必須項目を入力してください。
            {emptyCustomerInfo.map((info, index) => (
              <div key={index} style={{ display: "inline" }}>
                <br />
                <strong>{info}</strong>
              </div>
            ))}
          </>
        ),
      });
      return;
    }

    //電話番号桁数チェック
    if (data.phoneNumber.split("").length !== 11) {
      setOpenSnackbar({
        status: true,
        message: "11桁の電話番号を入力してください。",
      });
      return;
    }

    setLoadingOpen(true);
    updateCustomerInfo({
      ...data,
      customerId: customerId || data.customerId,
      siteId: siteId || data.siteId,
      updateDatetime: updateDatetime || data.updateDatetime,
    }).then((res) => {
      setLoadingOpen(false);
      if (res.data.result === -1) {
        //排他エラー処理
        setExclusionErrorMessage();
        return;
      }

      navigate(CUSTOMER_RESULT, {
        state: {
          header: "利用者情報変更",
          message: "利用者情報が変更されました。",
        },
      });
    });
  };

  const addNewFamily = () => {
    const samePersonCheckArray = data.families.map((f) => ({
      lastName: f.lastName,
      firstName: f.firstName,
      birthday: f.birthday,
    }));
    samePersonCheckArray.push({
      lastName: data.lastName,
      firstName: data.firstName,
      birthday: data.birthday,
    });
    navigate(FAMILY_ADDER, {
      state: {
        customerId: customerId || data.customerId,
        siteId: siteId || data.siteId,
        samePersonCheckArray,
      },
    });
  };

  const updateFamily = (row) => {
    const samePersonCheckArray = data.families.map((f) => ({
      lastName: f.lastName,
      firstName: f.firstName,
      birthday: f.birthday,
    }));
    samePersonCheckArray.push({
      lastName: data.lastName,
      firstName: data.firstName,
      birthday: data.birthday,
    });
    navigate(FAMILY_ADDER, {
      state: {
        ...row,
        customerId: customerId || data.customerId,
        siteId: siteId || data.siteId,
        samePersonCheckArray,
      },
    });
  };

  return (
    <>
      <Loading open={loadingOpen} />
      <Header title="利用者情報変更" />
      <Snackbar
        open={openSnackbar.status}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={handleSnackbarClose}
        sx={{ mt: "8vh" }}
      >
        <Alert
          severity="error"
          sx={{ width: "100%" }}
          onClose={handleSnackbarClose}
        >
          {openSnackbar.message}
        </Alert>
      </Snackbar>
      <Stack
        spacing="4vh"
        width="95%"
        marginTop="9vh"
        marginLeft="auto"
        marginRight="auto"
      >
        <Box
          sx={{ height: "46vh", overflowY: "scroll" }}
          onTouchStartCapture={(e) => {
            setEndY(0);
          }}
          onTouchMoveCapture={(e) => {
            setEndY(e.touches[0].clientY);
          }}
          onTouchEndCapture={(e) => {
            if (endY !== 0) {
              e.stopPropagation();
            }
          }}
        >
          <BaseInfoArea
            setLoadingOpen={setLoadingOpen}
            setOpenSnackbar={setOpenSnackbar}
            data={data}
            setData={setData}
          />
        </Box>
        <Stack
          sx={{ height: "26vh", overflowY: "scroll" }}
          onTouchStartCapture={(e) => {
            setEndY(0);
          }}
          onTouchMoveCapture={(e) => {
            setEndY(e.touches[0].clientY);
          }}
          onTouchEndCapture={(e) => {
            if (endY !== 0) {
              e.stopPropagation();
            }
          }}
        >
          <Typography variant="h6" align="left">
            利用者家族情報
          </Typography>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="none" sx={{ padding: "2%" }} align="left">
                    <Typography>No</Typography>
                  </TableCell>
                  <TableCell padding="none" sx={{ padding: "2%" }} align="left">
                    <Typography>名前</Typography>
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{ padding: "2%" }}
                    align="center"
                  >
                    <Typography>続柄</Typography>
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{ padding: "2%" }}
                    align="center"
                  >
                    <Typography>問診</Typography>
                  </TableCell>
                  <TableCell padding="none" sx={{ padding: "2%" }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {orderBy(data.families, ["familyId"]).map((row, index) => (
                  <TableRow key={index}>
                    <TableCell
                      padding="none"
                      sx={{ padding: "2%" }}
                      align="left"
                    >
                      {row.familyId}
                    </TableCell>
                    <TableCell
                      padding="none"
                      sx={{ padding: "2%" }}
                      align="left"
                    >
                      {`${row.lastName}${row.firstName}`}
                    </TableCell>
                    <TableCell
                      padding="none"
                      sx={{ padding: "2%" }}
                      align="center"
                    >
                      {row.relationshipClassText}
                    </TableCell>
                    <TableCell
                      padding="none"
                      sx={{ padding: "2%" }}
                      align="center"
                    >
                      {row.interviewFlg ? "済" : "未"}
                    </TableCell>
                    <TableCell
                      padding="none"
                      sx={{ padding: "2%" }}
                      align="center"
                    >
                      <EditIcon onTouchEndCapture={() => updateFamily(row)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!data.families && (
            <Typography variant="h6" align="center" marginTop="5vh">
              登録なし
            </Typography>
          )}
        </Stack>
      </Stack>
      <MainColorButton
        label="家族追加"
        style={{ ...bottomButton, width: "35%", marginLeft: "8%" }}
        onClick={addNewFamily}
      />
      <MainColorButton
        label="登録"
        style={{ ...bottomButton, width: "35%", marginLeft: "58%" }}
        onClick={updateInfo}
      />
    </>
  );
};

export default CustomerUpdater;
